import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Clock } from 'lucide-react';

const ContactUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: '#F9FAFB' }}>
      <header className="bg-white shadow-sm border-b" style={{ borderColor: '#E5E7EB' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition">
            <ArrowLeft size={20} />
            Back to Home
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Contact Us</h1>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Get in Touch</h2>
            <p className="text-gray-600 mb-8">
              Have questions about the admission process? We're here to help! Reach out to us through any of the following channels.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail size={24} color="#002147" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1" style={{ color: '#002147' }}>Email</h3>
                  <p className="text-gray-600">info@betinstitutions.org</p>
                  <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone size={24} color="#002147" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1" style={{ color: '#002147' }}>Phone</h3>
                  <p className="text-gray-600">+91 9663916334</p>
                  <p className="text-sm text-gray-500 mt-1">Monday to Friday, 9:00 AM - 4:00 PM</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin size={24} color="#002147" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1" style={{ color: '#002147' }}>Address</h3>
                  <p className="text-gray-600">
                    Bismillah Educational Trust<br />
                    12/5, C Cross Rd, Bismillahnagar<br />
                    S.G. Palya, Bengaluru<br />
                    Karnataka 560029, India
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock size={24} color="#002147" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1" style={{ color: '#002147' }}>Office Hours</h3>
                  <p className="text-gray-600">
                    Monday - Friday: 9:00 AM - 4:00 PM<br />
                    Saturday: Closed<br />
                    Sunday: Closed
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Frequently Asked Questions</h2>

            <div className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2" style={{ color: '#002147' }}>What is the application fee?</h3>
                <p className="text-gray-600 text-sm">
                  The application fee is ₹200, which is non-refundable and must be paid through our secure Razorpay payment gateway.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2" style={{ color: '#002147' }}>How do I receive my login credentials?</h3>
                <p className="text-gray-600 text-sm">
                  After successful payment, login credentials will be sent to your registered email and mobile number within 5 minutes.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2" style={{ color: '#002147' }}>What documents are required?</h3>
                <p className="text-gray-600 text-sm">
                  Mandatory: Father's and Mother's Aadhar copies. Optional: Birth certificate, Child's Aadhar, Passport size photo.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2" style={{ color: '#002147' }}>How long does the admission process take?</h3>
                <p className="text-gray-600 text-sm">
                  Initial review takes 2-3 business days. Interview scheduling within 5-7 days. Final decision within 2 weeks of interview.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2" style={{ color: '#002147' }}>Can I track my application status?</h3>
                <p className="text-gray-600 text-sm">
                  Yes! Login to your parent dashboard to view real-time status updates. You'll also receive email and SMS notifications.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2" style={{ color: '#002147' }}>Is the application fee refundable?</h3>
                <p className="text-gray-600 text-sm">
                  No, the ₹200 application fee is non-refundable. Please refer to our Cancellation and Refunds policy for more details.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2" style={{ color: '#002147' }}>I didn't receive my login credentials. What should I do?</h3>
                <p className="text-gray-600 text-sm">
                  Check your spam/junk folder first. If still not received within 10 minutes, contact us at info@betinstitutions.org with your payment details.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Support */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-8">
          <h2 className="text-2xl font-bold mb-4 text-center" style={{ fontFamily: 'Merriweather, serif', color: '#002147' }}>Technical Support</h2>
          <p className="text-center text-gray-700 mb-6">
            Experiencing technical issues with the portal? Our support team is here to help!
          </p>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold mb-2" style={{ color: '#002147' }}>24/7</div>
              <p className="text-sm text-gray-600">Email Support</p>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2" style={{ color: '#002147' }}>{'< 2hr'}</div>
              <p className="text-sm text-gray-600">Average Response Time</p>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2" style={{ color: '#002147' }}>100%</div>
              <p className="text-sm text-gray-600">Issue Resolution Rate</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;